import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionLabel, Button } from "../components/Primitives";

const MAX_ATTEMPTS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { checkAuth } = useAuth();
  const [state, setState] = useState(sessionId ? "checking" : "error");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        const ps = r.data.payment_status;
        if (ps === "paid") {
          setState("paid");
          checkAuth();
          return;
        }
        if (ps === "failed" || ps === "expired") {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      if (attempts.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, checkAuth]);

  return (
    <div className="mx-auto max-w-xl px-6 py-20 md:px-12" data-testid="payment-success-page">
      <div className="border border-edge bg-ash p-8 text-center md:p-10">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-gold" />
            <SectionLabel>Confirming Payment</SectionLabel>
            <p className="mt-3 text-muted" data-testid="payment-status-text">Hold tight — we're confirming your payment with Stripe…</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
            <SectionLabel>Payment Confirmed</SectionLabel>
            <h1 className="mt-2 font-serif text-3xl font-semibold text-bone">Welcome to Premium</h1>
            <p className="mt-4 text-muted" data-testid="payment-status-text">
              Your $3/month subscription is active — a receipt from Stripe is on its way to your email.
              Lessons, Quizzes, Assignments, the AI Tutor and Certificates are now unlocked.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/lessons"><Button variant="gold" data-testid="payment-success-lessons-btn">Start Learning <ArrowRight className="h-4 w-4" /></Button></Link>
              <Link to="/dashboard"><Button variant="outline" data-testid="payment-success-dashboard-btn">Go to Dashboard</Button></Link>
            </div>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <AlertTriangle className="mx-auto h-10 w-10 text-gold" />
            <SectionLabel>{state === "timeout" ? "Still Processing" : "Something Went Wrong"}</SectionLabel>
            <p className="mt-3 text-muted" data-testid="payment-status-text">
              {state === "timeout"
                ? "Your payment is still processing. Check your email for a Stripe receipt — your access will unlock automatically once it clears."
                : "We couldn't confirm this payment. If you were charged, your access will unlock automatically — otherwise, please try again."}
            </p>
            <div className="mt-8 flex justify-center">
              <Link to="/premium"><Button variant="outline" data-testid="payment-retry-btn">Back to Premium</Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
